/**
 * @fileoverview Shared outline icon set for the sales screens.
 * @description One stroke-style SVG per name (24px grid, rounded caps), drawn in `currentColor` so
 * the caller's text color tints it. Used by the KPI cards and the empty/error state blocks so the
 * same shapes aren't redrawn inline on every page.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import type { ReactNode } from 'react'

export type IconName =
  | 'alert'
  | 'briefcase'
  | 'check'
  | 'filter'
  | 'grid'
  | 'hourglass'
  | 'inbox'
  | 'info'
  | 'search'
  | 'shield'
  | 'trend'
  | 'userCheck'
  | 'users'

interface IconProps {
  name: IconName
  size?: number
  strokeWidth?: number
}

const ICON_PATHS: Record<IconName, ReactNode> = {
  alert: (
    <>
      <path d="M12 3.5l9.2 15.7H2.8z" />
      <path d="M12 10v4.4" />
      <path d="M12 17.1v.1" />
    </>
  ),
  briefcase: (
    <>
      <rect x="3" y="7.5" width="18" height="12" rx="2.2" />
      <path d="M8.5 7.5V5.8a1.8 1.8 0 011.8-1.8h3.4a1.8 1.8 0 011.8 1.8v1.7" />
    </>
  ),
  check: <path d="M5 12.5l4.5 4.5L19 7.5" />,
  filter: <path d="M3.5 5h17l-6.6 7.6V19l-3.8 2v-8.4z" />,
  grid: (
    <>
      <rect x="3.5" y="3.5" width="7" height="7" rx="1.4" />
      <rect x="13.5" y="3.5" width="7" height="7" rx="1.4" />
      <rect x="3.5" y="13.5" width="7" height="7" rx="1.4" />
      <rect x="13.5" y="13.5" width="7" height="7" rx="1.4" />
    </>
  ),
  hourglass: (
    <>
      <path d="M7 3.5h10M7 20.5h10" />
      <path d="M8 3.5c0 4 4 5 4 8.5s-4 4.5-4 8.5" />
      <path d="M16 3.5c0 4-4 5-4 8.5s4 4.5 4 8.5" />
    </>
  ),
  inbox: (
    <>
      <path d="M3.5 13l2.7-7.5h11.6L20.5 13" />
      <path d="M3.5 13v5.5h17V13h-4.6a3.9 3.9 0 01-7.8 0z" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 8v4.5" />
      <path d="M12 15.6v.1" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="6.5" />
      <path d="M16 16l4.5 4.5" />
    </>
  ),
  shield: (
    <>
      <path d="M12 3l7.5 3v5.5c0 4.6-3.1 8-7.5 9.5-4.4-1.5-7.5-4.9-7.5-9.5V6z" />
      <path d="M9 12l2.2 2.2L15.5 10" />
    </>
  ),
  trend: (
    <>
      <path d="M3.5 17l5.5-5.5 3.5 3.5 7.5-7.5" />
      <path d="M15 7.5h5.5V13" />
    </>
  ),
  userCheck: (
    <>
      <circle cx="10" cy="8" r="3.3" />
      <path d="M4 20c0-3.2 2.6-5.4 5.8-5.4 1.3 0 2.5.3 3.5 1" />
      <path d="M15.4 18.4l1.9 1.9 3.3-3.8" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="3.3" />
      <path d="M3.2 20c0-3.2 2.6-5.4 5.8-5.4s5.8 2.2 5.8 5.4" />
      <path d="M17 11.4a2.9 2.9 0 100-5.8" />
      <path d="M18 20c0-2.5-1-4.2-2.5-5" />
    </>
  ),
}

/**
 * @component
 * @description Renders one named outline icon, sized and tinted by the caller.
 * @param {IconProps} props - The icon to draw.
 * @param {IconName} props.name - Which icon.
 * @param {number} [props.size] - Width and height in pixels (default 20).
 * @param {number} [props.strokeWidth] - Stroke width on the 24px grid (default 1.9).
 */
function Icon({ name, size = 20, strokeWidth = 1.9 }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {ICON_PATHS[name]}
    </svg>
  )
}

export default Icon
